import React from "react";

import type { Service, ServiceOption } from "@/lib/api/types";
import { formatMinutes } from "@/lib/helpers";
import {
  formatMoney,
  formatServicePrice,
  getEffectivePrice,
  getServiceDescription,
  hasStartingAtPrice,
} from "@/lib/helpers/servicePricing";
import { groupIdenticalServiceOccurrences } from "@/lib/helpers/serviceGrouping";

import styles from "./styles.module.scss";

export type BookingService = Service & { option?: ServiceOption };

interface BookingServiceSummaryProps {
  services: BookingService[];
  ariaLabel?: string;
  compact?: boolean;
  showDescriptions?: boolean;
}

const BookingServiceSummary = ({
  services,
  ariaLabel = "Selected services",
  compact = false,
  showDescriptions = false,
}: BookingServiceSummaryProps) => {
  const totalPrice = services.reduce(
    (total, service) => total + getEffectivePrice(service),
    0,
  );
  const totalDuration = services.reduce(
    (total, service) =>
      total + (service.option?.duration ?? service.duration),
    0,
  );
  const totalIsStartingAt = services.some(hasStartingAtPrice);
  const groupedServices = groupIdenticalServiceOccurrences(services);
  const summaryClassName = [
    styles.summary,
    compact ? styles.compact : null,
    showDescriptions ? styles.withDescriptions : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={summaryClassName}>
      <ul className={styles.serviceList} aria-label={ariaLabel}>
        {groupedServices.map(({ service, quantity, key }) => {
          const price = formatServicePrice(service);
          const duration = service.option?.duration ?? service.duration;
          const description = showDescriptions
            ? getServiceDescription(service)
            : null;
          const showServiceName = !compact || services.length > 1;

          return (
            <li
              className={styles.serviceItem}
              key={key}
            >
              <div
                className={
                  showServiceName
                    ? styles.serviceRow
                    : `${styles.serviceRow} ${styles.metaOnlyRow}`
                }
              >
                {showServiceName && (
                  <span className={styles.serviceName}>
                    <span className={styles.serviceTitle}>
                      {service.title}
                      {quantity > 1 && (
                        <>
                          <span className={styles.quantity} aria-hidden="true">
                            {" "}× {quantity}
                          </span>
                          <span className={styles.visuallyHidden}>
                            {" "}Quantity {quantity}
                          </span>
                        </>
                      )}
                    </span>
                    {service.option && !compact && (
                      <span className={styles.optionTitle}>
                        {service.option.title}
                      </span>
                    )}
                  </span>
                )}
                <span className={styles.serviceMeta}>
                  <span className={styles.price}>
                    {price ? (
                      <>
                        {hasStartingAtPrice(service) && (
                          <span className={styles.visuallyHidden}>
                            Starting at{" "}
                          </span>
                        )}
                        <span>{price}</span>
                      </>
                    ) : (
                      <>
                        <span className={styles.visuallyHidden}>
                          Price unavailable
                        </span>
                        <span aria-hidden="true">—</span>
                      </>
                    )}
                  </span>
                  <span className={styles.separator} aria-hidden="true">
                    ·
                  </span>
                  <span className={styles.duration}>
                    <span className={styles.visuallyHidden}>Duration </span>
                    {formatMinutes(duration)}
                  </span>
                </span>
              </div>
              {description && (
                <p className={styles.description}>{description}</p>
              )}
            </li>
          );
        })}
      </ul>

      {services.length > 1 && (
        <div
          className={styles.totalRow}
          role="group"
          aria-label="Service total"
        >
          <span>Total</span>
          <span className={styles.serviceMeta}>
            <span className={styles.price}>
              {totalIsStartingAt && (
                <span className={styles.visuallyHidden}>Starting at </span>
              )}
              <span>
                {formatMoney(totalPrice)}
                {totalIsStartingAt ? "+" : ""}
              </span>
            </span>
            <span className={styles.separator} aria-hidden="true">
              ·
            </span>
            <span className={styles.duration}>
              <span className={styles.visuallyHidden}>Duration </span>
              {formatMinutes(totalDuration)}
            </span>
          </span>
        </div>
      )}
    </div>
  );
};

export default BookingServiceSummary;
